import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api, ApiError } from '@/lib/api'
import { useAuth } from '@/lib/auth'
import { useCountry } from '@/lib/country'
import { date, money } from '@/lib/format'
import { combine, useAsync } from '@/lib/useAsync'
import { ResolvedSteps } from '@/components/ChainPreview'
import { Dialog } from '@/components/ui/dialog'
import { PageHeader } from '@/components/ui/page'
import {
  Badge,
  Button,
  Card,
  CardHeader,
  cx,
  EmptyState,
  ErrorState,
  Loading,
} from '@/components/ui/primitives'
import { Checkbox, Field, FieldRow, Input, MoneyInput, Select } from '@/components/ui/form'
import { Table, Td, Th, Tr } from '@/components/ui/table'
import { useToast } from '@/components/ui/toast'
import type {
  ApprovalChainDetail,
  ApprovalChainListItem,
  ApprovalChainStepDetail,
  Paged,
  ResolveApprovalChainResponse,
  RoleSummary,
  UserListItem,
} from '@/types/api'

/**
 * Who approves what, per country.
 *
 * <p>
 * Everything on this page is a rule about who may commit the company to a price. A chain
 * saved wrong does not misbehave: it stops every quotation in that country, or sends them
 * to somebody who should never have seen them. So the page states the invariant - every
 * quotation has an approver - in the positive, and says so before Save rather than after.
 * </p>
 */
export function ApprovalChains() {
  const { can } = useAuth()
  const navigate = useNavigate()
  const [creating, setCreating] = useState(false)

  const list = useAsync<ApprovalChainListItem[]>(
    (signal) => api.get<ApprovalChainListItem[]>('/approval-chains', undefined, signal),
    [],
  )

  return (
    <>
      <PageHeader
        title="Approval Chains"
        hint="One active chain per country. Every quotation runs one, whatever its value."
        actions={
          can('approvalchain.create') && (
            <Button size="sm" variant="primary" onClick={() => setCreating(true)}>
              New chain
            </Button>
          )
        }
      />

      <NewChain
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={(id) => {
          setCreating(false)
          list.reload()

          // Straight into the editor. A chain with no steps approves nothing, and it is
          // inactive until somebody says otherwise, so leaving it on the listing is
          // leaving a job half done.
          navigate(`/approval-chains/${id}`)
        }}
      />

      {list.loading && <Loading />}
      {list.error && <ErrorState title={list.error.title} detail={list.error.detail} onRetry={list.reload} />}

      {list.data && list.data.length === 0 && (
        <Card>
          <EmptyState
            title="No approval chains"
            hint="A country without a chain cannot submit anything. One is seeded for each country automatically."
            action={
              can('approvalchain.create') && (
                <Button size="sm" onClick={() => setCreating(true)}>
                  New chain
                </Button>
              )
            }
          />
        </Card>
      )}

      {list.data && list.data.length > 0 && (
        <Card>
          <Table>
            <thead>
              <tr>
                <Th>Country</Th>
                <Th>Chain</Th>
                <Th>Steps</Th>
                <Th>Effective</Th>
                <Th>Status</Th>
              </tr>
            </thead>
            <tbody>
              {list.data.map((chain) => (
                <Tr key={chain.id} onClick={() => navigate(`/approval-chains/${chain.id}`)}>
                  <Td>
                    <span className="font-medium">{chain.countryName}</span>
                    <span className="ml-2 font-mono text-[11px] text-faint">{chain.countryIsoCode}</span>
                  </Td>
                  <Td>{chain.name}</Td>
                  <Td className="tabular">{chain.stepCount}</Td>
                  <Td>{date(chain.effectiveFrom)}</Td>
                  <Td>
                    <Badge tone={chain.isActive ? 'positive' : 'neutral'}>
                      {chain.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </Td>
                </Tr>
              ))}
            </tbody>
          </Table>
        </Card>
      )}

      {!can('approvalchain.edit') && (
        <p className="mt-3 text-[12.5px] text-muted">
          You can read these. Changing one is for the Owner and the Director of Sales.
        </p>
      )}
    </>
  )
}

/**
 * Standing up a chain for a country.
 *
 * <p>
 * A chain is created empty and inactive, which is the point: it is a draft until somebody
 * reads it and activates it, and activation refuses a chain that would leave a quotation
 * with no approver. A country already has a seeded chain, so this is for the succession -
 * the new policy written alongside the running one, tried, then stood up in its place.
 * </p>
 */
function NewChain({
  open,
  onClose,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  onCreated: (id: string) => void
}) {
  const { available, current } = useCountry()
  const toast = useToast()

  const [countryId, setCountryId] = useState(current?.countryId ?? '')
  const [name, setName] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [saving, setSaving] = useState(false)

  async function create() {
    setSaving(true)
    try {
      const created = await api.post<{ id: string }>('/approval-chains', {
        countryId: countryId || current?.countryId,
        name: name.trim(),
        effectiveFrom: effectiveFrom || null,
      })

      setName('')
      setEffectiveFrom('')
      onCreated(created.id)
    } catch (cause) {
      toast.show(
        cause instanceof ApiError ? (cause.detail ?? cause.title) : 'Could not create the chain',
        'error',
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog
      open={open}
      title="New approval chain"
      description="Created empty and inactive. Add its steps, try it, then activate it - which stands the country's current chain down in the same move."
      onClose={onClose}
      footer={
        <>
          <Button size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant="primary"
            loading={saving}
            disabled={!name.trim() || !(countryId || current)}
            onClick={() => void create()}
          >
            Create chain
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Country" required hint="Approval is per country, like everything else that commits Codemax to a price.">
          {(id) => (
            <Select id={id} value={countryId} onChange={(event) => setCountryId(event.target.value)}>
              <option value="">Choose a country</option>
              {available.map((country) => (
                <option key={country.countryId} value={country.countryId}>
                  {country.name} ({country.isoCode})
                </option>
              ))}
            </Select>
          )}
        </Field>

        <Field
          label="Name"
          required
          hint="What makes this chain different from the one it replaces - 'From January 2027', 'Two-step above 100k'."
        >
          {(id) => (
            <Input
              id={id}
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="Malaysia approval chain"
            />
          )}
        </Field>

        <Field label="Effective from" hint="For the record. Which chain runs is decided by which one is active, not by this date.">
          {(id) => (
            <Input
              id={id}
              type="date"
              value={effectiveFrom}
              onChange={(event) => setEffectiveFrom(event.target.value)}
            />
          )}
        </Field>
      </div>
    </Dialog>
  )
}

/**
 * What this chain does to a quotation, before there is a quotation.
 *
 * <p>
 * Everything above this is a rule stated in the abstract; this is the rule applied. An
 * administrator can write a chain that looks right and sends a 250,000 deal to a Manager who
 * left, or one whose only step asks for a manager the preparer does not have. Both read fine
 * on the rail. Both stop a rep at submit.
 * </p>
 *
 * The answer comes from the server rather than from a copy of the rules here, on purpose:
 * one resolver, run by submit and by this, so what an administrator signs off on is what a
 * rep gets.
 */
function Simulator({
  chainId,
  countryIsoCode,
  currencyCode,
  users,
  dirty,
}: {
  chainId: string
  countryIsoCode: string
  currencyCode: string
  users: UserListItem[]
  dirty: boolean
}) {
  const [value, setValue] = useState('50000')
  const [discount, setDiscount] = useState('0')
  const [amendedTerms, setAmendedTerms] = useState(false)
  const [preparedBy, setPreparedBy] = useState('')
  const [running, setRunning] = useState(false)
  const [result, setResult] = useState<ResolveApprovalChainResponse | undefined>()
  const [failed, setFailed] = useState<string | undefined>()

  async function run() {
    setRunning(true)
    setFailed(undefined)

    try {
      setResult(
        await api.post<ResolveApprovalChainResponse>(`/approval-chains/${chainId}/resolve`, {
          subtotalNet: Number(value || 0),
          maxLineDiscountPercent: Number(discount || 0),
          hasTermsOverride: amendedTerms,
          preparedByUserId: preparedBy || null,
        }),
      )
    } catch (cause) {
      setResult(undefined)
      setFailed(cause instanceof ApiError ? (cause.detail ?? cause.title) : 'That did not work.')
    } finally {
      setRunning(false)
    }
  }

  return (
    <Card className="mt-4">
      <CardHeader
        title="Try it"
        hint="Answered by the same code that runs at submit, against the saved chain."
      />

      <div className="px-5 py-4">
        <FieldRow>
        <Field row label={`Value (${currencyCode})`} hint="Net of discount, before tax.">
          {(id) => (
            <MoneyInput
              id={id}
              value={value}
              className="w-36"
              onChange={(event) => setValue(event.target.value)}
            />
          )}
        </Field>

        <Field row label="Deepest line discount" hint="On any one line, as a percentage.">
          {(id) => (
            <Input
              id={id}
              type="number"
              step="0.01"
              value={discount}
              className="w-28 text-right tabular"
              onChange={(event) => setDiscount(event.target.value)}
            />
          )}
        </Field>

        <Field row label="Raised by" hint="Manager steps resolve against this person's reporting line.">
          {(id) => (
            <Select
              id={id}
              value={preparedBy}
              className="w-52"
              onChange={(event) => setPreparedBy(event.target.value)}
            >
              <option value="">Me</option>
              {users
                // Only people who could actually raise one here. The API refuses the rest,
                // and an answer about somebody with no access to this country is not an
                // answer about this chain.
                .filter((user) => user.isActive && user.countries.includes(countryIsoCode))
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName}
                  </option>
                ))}
            </Select>
          )}
        </Field>

        {/* On the control row, by sitting in the row rather than by being nudged
            towards it: the empty span takes the label row and the hint row is left
            alone underneath. */}
        <div className="row-span-3 grid grid-rows-subgrid gap-1.5">
          <span aria-hidden />
          <div className="flex items-center gap-3">
            <Checkbox
              label="Amended terms"
              checked={amendedTerms}
              onChange={(event) => setAmendedTerms(event.target.checked)}
            />

            <Button size="sm" loading={running} onClick={() => void run()}>
              Resolve
            </Button>
          </div>
        </div>
        </FieldRow>
      </div>

      {dirty && (
        <p className="mx-5 mb-4 rounded-md bg-warn-wash px-3 py-2 text-[12.5px] text-warn">
          This runs the saved chain. Save first, or you are trying the one you are replacing.
        </p>
      )}

      {(result || failed) && (
        <div className="border-t border-line-soft px-5 py-4">
          {failed && <p className="text-[13px] text-crit">{failed}</p>}

          {/* A chain that resolves to nobody is a fault, never a quotation nobody approves -
              so this is the same red the rep would be stopped by, not a warning. */}
          {result?.blocker && (
            <p className="mb-3 rounded-md bg-crit-wash px-3 py-2 text-[13px] text-crit">
              {result.blocker}
            </p>
          )}

          {result && result.steps.length === 0 && !result.blocker && (
            <p className="text-[13px] text-muted">This chain resolves to nobody at all.</p>
          )}

          {result && result.steps.length > 0 && <ResolvedSteps steps={result.steps} />}
        </div>
      )}
    </Card>
  )
}

/** What a step asks for, as one control. The three are mutually exclusive by construction. */
type ApproverKind = 'manager' | 'user' | 'role'

function kindOf(step: ApprovalChainStepDetail): ApproverKind {
  if (step.approverIsPreparersManager) return 'manager'
  return step.approverUserId ? 'user' : 'role'
}

/** The rail, and everything that writes to it. */
/** A chain at its own address. See ProductDetailPage for why. */
export function ApprovalChainDetailPage() {
  const { id } = useParams()
  return id ? (
    <ChainEditor chainId={id} />
  ) : (
    <ErrorState title="No chain" detail="That link is missing an approval chain id." />
  )
}

function ChainEditor({ chainId }: { chainId: string }) {
  const navigate = useNavigate()
  const onDone = () => navigate('/approval-chains')
  const { can } = useAuth()
  const { available } = useCountry()
  const toast = useToast()
  const editable = can('approvalchain.edit')

  const [steps, setSteps] = useState<ApprovalChainStepDetail[] | undefined>()
  const [dirty, setDirty] = useState(false)
  const [saving, setSaving] = useState(false)
  const [problems, setProblems] = useState<string[]>([])
  const [dragging, setDragging] = useState<string | null>(null)
  const [over, setOver] = useState<string | null>(null)

  const chain = useAsync<ApprovalChainDetail>(
    async (signal) => {
      const loaded = await api.get<ApprovalChainDetail>(`/approval-chains/${chainId}`, undefined, signal)
      setSteps(loaded.steps)
      setDirty(false)
      return loaded
    },
    [chainId],
  )

  const users = useAsync<Paged<UserListItem>>(
    (signal) => api.get<Paged<UserListItem>>('/users', { pageSize: 200 }, signal),
    [],
  )

  const roles = useAsync<RoleSummary[]>(
    (signal) => api.get<RoleSummary[]>('/roles', undefined, signal),
    [],
  )

  function change(id: string, patch: Partial<ApprovalChainStepDetail>) {
    setSteps((current) => current?.map((s) => (s.id === id ? { ...s, ...patch } : s)))
    setDirty(true)
  }

  function reorder(from: number, to: number) {
    setSteps((current) => {
      if (!current || from === to || to < 0 || to >= current.length) return current

      const next = [...current]
      const [moved] = next.splice(from, 1)
      if (moved === undefined) return current

      next.splice(to, 0, moved)
      return next
    })
    setDirty(true)
  }

  function addStep() {
    setSteps((current) => [
      ...(current ?? []),
      {
        // A client-side placeholder: the server assigns real ids when the list is written.
        id: `new-${(current?.length ?? 0) + 1}-${Date.now()}`,
        stepOrder: (current?.length ?? 0) + 1,
        approverUserId: null,
        approverUserName: null,
        approverRoleId: null,
        approverRoleName: null,
        approverIsPreparersManager: true,
        minAmount: null,
        maxAmount: null,
        triggerDiscountPercent: null,
        triggerOnTermsOverride: false,
        isMandatory: true,
        firesAlways: true,
      },
    ])
    setDirty(true)
  }

  async function save() {
    if (!steps) return

    setSaving(true)
    setProblems([])

    try {
      await api.put(`/approval-chains/${chainId}/steps`, {
        // Position is the order, so the numbers are rewritten from it rather than carried.
        steps: steps.map((step, index) => ({
          stepOrder: index + 1,
          approverUserId: step.approverIsPreparersManager ? null : step.approverUserId,
          approverRoleId: step.approverIsPreparersManager ? null : step.approverRoleId,
          approverIsPreparersManager: step.approverIsPreparersManager,
          minAmount: step.minAmount,
          maxAmount: step.maxAmount,
          triggerDiscountPercent: step.triggerDiscountPercent,
          triggerOnTermsOverride: step.triggerOnTermsOverride,
          isMandatory: step.isMandatory,
        })),
      })

      toast.show('Chain saved')
      chain.reload()
    } catch (error) {
      // The API answers with every problem at once so an administrator fixes the chain in
      // one pass. Shown in its own words rather than reworded here.
      // The API answers with every problem joined into the detail, so an administrator
      // fixes the chain in one pass rather than one refusal at a time.
      setProblems([
        error instanceof ApiError
          ? (error.detail ?? error.title)
          : error instanceof Error
            ? error.message
            : 'Save failed.',
      ])
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive() {
    if (!chain.data) return

    const action = chain.data.isActive ? 'deactivate' : 'activate'

    try {
      await api.post(`/approval-chains/${chainId}/${action}`, {})
      toast.show(chain.data.isActive ? 'Chain deactivated' : 'Chain activated')
      chain.reload()
    } catch (error) {
      toast.show(error instanceof Error ? error.message : 'That did not work', 'error')
    }
  }

  // The pickers are part of the chain, not decoration around it: a rail drawn before the
  // people and roles arrive shows every step as "Unassigned" and invites an administrator
  // to fix a chain that was never broken.
  const gate = combine(chain, users, roles)

  if (gate.loading) return <Loading />
  if (gate.error) return <ErrorState title={gate.error.title} detail={gate.error.detail} onRetry={gate.reload} />
  if (!chain.data || !steps) return null

  // The invariant the whole product rests on, stated in the positive. A chain of nothing
  // but conditional steps looks finished and lets a small undiscounted quotation reach
  // nobody, so it is said here rather than discovered at Save.
  const country = available.find((c) => c.countryId === chain.data?.countryId)

  const covered = steps.some(
    (s) =>
      s.isMandatory &&
      s.minAmount === null &&
      s.maxAmount === null &&
      s.triggerDiscountPercent === null &&
      !s.triggerOnTermsOverride,
  )

  return (
    <>
      <PageHeader
        title={chain.data.name}
        hint={`Effective ${date(chain.data.effectiveFrom)} · drag a stop to reorder`}
        back={{ label: 'Approval chains', onClick: onDone }}
      />

      <Card>
        <CardHeader
          title="Steps"
          hint="Order is who is asked first. A hollow stop is advisory: told about the quotation, does not decide it."
          action={
            editable && (
              <div className="flex gap-2">
                <Button size="sm" variant="ghost" disabled={saving} onClick={() => void toggleActive()}>
                  {chain.data.isActive ? 'Deactivate' : 'Activate'}
                </Button>
                <Button size="sm" variant="ghost" disabled={saving} onClick={addStep}>
                  Add step
                </Button>
                <Button size="sm" disabled={saving || !dirty} onClick={() => void save()}>
                  {saving ? 'Saving…' : 'Save'}
                </Button>
              </div>
            )
          }
        />

        {problems.length > 0 && (
          <ul className="mx-5 mt-4 list-disc rounded-md bg-crit-wash px-8 py-3 text-[13px] text-crit">
            {problems.map((problem) => (
              <li key={problem}>{problem}</li>
            ))}
          </ul>
        )}

        <div className="px-5 py-4">
          {steps.length === 0 && (
            <EmptyState
              title="No steps"
              hint="A country whose chain names nobody cannot submit a quotation at all."
            />
          )}

          {steps.map((step, index) => (
            <StepStop
              key={step.id}
              step={step}
              index={index}
              count={steps.length}
              editable={editable}
              users={users.data?.items ?? []}
              roles={roles.data ?? []}
              dragging={dragging === step.id}
              over={over === step.id && dragging !== step.id}
              onDragStart={() => setDragging(step.id)}
              onDragOver={() => setOver(step.id)}
              onDragEnd={() => {
                setDragging(null)
                setOver(null)
              }}
              onDrop={() => {
                const from = steps.findIndex((s) => s.id === dragging)
                setDragging(null)
                setOver(null)
                reorder(from, index)
              }}
              onNudge={(delta) => reorder(index, index + delta)}
              onChange={(patch) => change(step.id, patch)}
              onRemove={() => {
                setSteps((current) => current?.filter((s) => s.id !== step.id))
                setDirty(true)
              }}
            />
          ))}
        </div>

        <div
          className={cx(
            'flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t px-5 py-3 text-[12.5px]',
            covered ? 'border-line-soft text-muted' : 'border-crit/30 bg-crit-wash text-crit',
          )}
        >
          <span>
            {steps.length} step{steps.length === 1 ? '' : 's'}
            {dirty && ' · unsaved'}
          </span>

          <span className="font-medium">
            {covered
              ? 'Every quotation has an approver'
              : 'No step fires on every quotation - this chain cannot be saved'}
          </span>
        </div>
      </Card>

      <Simulator
        chainId={chainId}
        countryIsoCode={country?.isoCode ?? ''}
        currencyCode={country?.currencyCode ?? ''}
        users={users.data?.items ?? []}
        dirty={dirty}
      />
    </>
  )
}

/** One stop on the rail. */
function StepStop({
  step,
  index,
  count,
  editable,
  users,
  roles,
  dragging,
  over,
  onDragStart,
  onDragOver,
  onDragEnd,
  onDrop,
  onNudge,
  onChange,
  onRemove,
}: {
  step: ApprovalChainStepDetail
  index: number
  count: number
  editable: boolean
  users: UserListItem[]
  roles: RoleSummary[]
  dragging: boolean
  over: boolean
  onDragStart: () => void
  onDragOver: () => void
  onDragEnd: () => void
  onDrop: () => void
  onNudge: (delta: number) => void
  onChange: (patch: Partial<ApprovalChainStepDetail>) => void
  onRemove: () => void
}) {
  const [armed, setArmed] = useState(false)

  const kind = kindOf(step)
  const first = index === 0
  const last = index === count - 1

  function setKind(next: ApproverKind) {
    onChange({
      approverIsPreparersManager: next === 'manager',
      approverUserId: next === 'user' ? (step.approverUserId ?? users[0]?.id ?? null) : null,
      approverRoleId: next === 'role' ? (step.approverRoleId ?? roles[0]?.id ?? null) : null,
    })
  }

  return (
    <div
      draggable={armed}
      onDragStart={(event) => {
        event.dataTransfer.setData('text/plain', step.id)
        event.dataTransfer.effectAllowed = 'move'
        onDragStart()
      }}
      onDragOver={(event) => {
        event.preventDefault()
        event.dataTransfer.dropEffect = 'move'
        onDragOver()
      }}
      onDrop={(event) => {
        event.preventDefault()
        setArmed(false)
        onDrop()
      }}
      onDragEnd={() => {
        setArmed(false)
        onDragEnd()
      }}
      className={cx(
        'grid grid-cols-[26px_1fr] gap-x-3 rounded-md',
        dragging && 'opacity-40',
        over && 'bg-accent-wash/60',
      )}
    >
      {/* The gutter carries the rail: a segment above the dot unless this is the first stop,
          one below unless it is the last. */}
      <div className="relative flex justify-center pt-4">
        {!first && <span className="absolute left-1/2 top-0 h-4 w-px -translate-x-1/2 bg-line" />}
        {!last && <span className="absolute left-1/2 bottom-0 top-6 w-px -translate-x-1/2 bg-line" />}
        <span
          className={cx(
            'z-10 h-2.5 w-2.5 rounded-full border-2 border-spark',
            // Filled blocks the quotation; hollow is only told about it. Same vocabulary as
            // the payment schedule on the document.
            step.isMandatory ? 'bg-spark' : 'bg-surface',
          )}
        />
      </div>

      <div className="py-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-1.5">
            {editable && (
              <button
                type="button"
                aria-label={`Move step ${index + 1} of ${count}`}
                title="Drag to reorder, or focus and use the arrow keys"
                onPointerDown={() => setArmed(true)}
                onPointerUp={() => setArmed(false)}
                onKeyDown={(event) => {
                  if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') return
                  event.preventDefault()
                  onNudge(event.key === 'ArrowUp' ? -1 : 1)
                }}
                className="cursor-grab select-none px-1 leading-none text-faint hover:text-muted focus-visible:text-muted"
              >
                ⠿
              </button>
            )}

            {editable ? (
              <>
                <Select
                  aria-label="Approver"
                  value={kind}
                  className="h-8 w-44 min-w-44 py-0 text-[12.5px]"
                  onChange={(event) => setKind(event.target.value as ApproverKind)}
                >
                  <option value="manager">The preparer's manager</option>
                  <option value="user">A specific person</option>
                  <option value="role">Anyone holding a role</option>
                </Select>

                {kind === 'user' && (
                  <Select
                    aria-label="Person"
                    value={step.approverUserId ?? ''}
                    className="h-8 w-52 min-w-52 py-0 text-[12.5px]"
                    onChange={(event) => onChange({ approverUserId: event.target.value })}
                  >
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.displayName}
                      </option>
                    ))}
                  </Select>
                )}

                {kind === 'role' && (
                  <Select
                    aria-label="Role"
                    value={step.approverRoleId ?? ''}
                    className="h-8 w-52 min-w-52 py-0 text-[12.5px]"
                    onChange={(event) => onChange({ approverRoleId: event.target.value })}
                  >
                    {roles.map((role) => (
                      <option key={role.id} value={role.id}>
                        {role.name}
                      </option>
                    ))}
                  </Select>
                )}
              </>
            ) : (
              <span className="text-[13.5px] font-medium">
                {step.approverIsPreparersManager
                  ? "The preparer's manager"
                  : (step.approverUserName ?? `Any ${step.approverRoleName ?? 'role'}`)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-1.5">
            {editable ? (
              <Select
                aria-label="Blocking"
                value={step.isMandatory ? 'mandatory' : 'advisory'}
                className="h-8 w-28 min-w-28 py-0 text-[12.5px]"
                onChange={(event) => onChange({ isMandatory: event.target.value === 'mandatory' })}
              >
                <option value="mandatory">Mandatory</option>
                <option value="advisory">Advisory</option>
              </Select>
            ) : (
              <Badge tone={step.isMandatory ? 'accent' : 'neutral'}>
                {step.isMandatory ? 'Mandatory' : 'Advisory'}
              </Badge>
            )}

            {editable && (
              <button
                type="button"
                aria-label="Remove step"
                onClick={onRemove}
                className="px-1 text-faint hover:text-crit focus-visible:text-crit"
              >
                ×
              </button>
            )}
          </div>
        </div>

        <Triggers step={step} editable={editable} onChange={onChange} />
      </div>
    </div>
  )
}

/**
 * When the step fires.
 *
 * A step with none of these set fires on everything, which is what makes a chain valid.
 * Written as toggles rather than a mode dropdown because they compose: one step can be both
 * the amount-band approver and the terms-override approver.
 */
function Triggers({
  step,
  editable,
  onChange,
}: {
  step: ApprovalChainStepDetail
  editable: boolean
  onChange: (patch: Partial<ApprovalChainStepDetail>) => void
}) {
  const band = step.minAmount !== null || step.maxAmount !== null
  const discount = step.triggerDiscountPercent !== null

  if (!editable) {
    return (
      <div className="mt-2 flex flex-wrap gap-1.5 text-[12px] text-muted">
        {step.firesAlways && <Badge tone="accent">Every quotation</Badge>}
        {band && (
          <Badge tone="neutral">
            {step.minAmount !== null ? money(step.minAmount, '', 2) : '0'} to{' '}
            {step.maxAmount !== null ? money(step.maxAmount, '', 2) : 'no ceiling'}
          </Badge>
        )}
        {discount && <Badge tone="neutral">Discount above {step.triggerDiscountPercent}%</Badge>}
        {step.triggerOnTermsOverride && <Badge tone="neutral">Amended terms</Badge>}
      </div>
    )
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-1.5">
      <Chip
        on={step.firesAlways}
        label="Every quotation"
        onClick={() =>
          onChange({
            minAmount: null,
            maxAmount: null,
            triggerDiscountPercent: null,
            triggerOnTermsOverride: false,
          })
        }
      />

      <Chip
        on={band}
        label="Value band"
        onClick={() => onChange(band ? { minAmount: null, maxAmount: null } : { minAmount: 0 })}
      />

      {band && (
        <>
          <Input
            aria-label="From"
            type="number"
            step="0.01"
            defaultValue={step.minAmount ?? 0}
            className="h-8 w-28 min-w-28 py-0 text-right tabular"
            onBlur={(event) =>
              onChange({ minAmount: event.target.value === '' ? null : Number(event.target.value) })
            }
          />
          <span className="font-mono text-[11px] uppercase tracking-[0.08em] text-muted">to</span>
          <Input
            aria-label="To"
            type="number"
            step="0.01"
            placeholder="no ceiling"
            defaultValue={step.maxAmount ?? ''}
            className="h-8 w-28 min-w-28 py-0 text-right tabular"
            onBlur={(event) =>
              onChange({ maxAmount: event.target.value === '' ? null : Number(event.target.value) })
            }
          />
        </>
      )}

      <Chip
        on={discount}
        label="Discount above"
        onClick={() => onChange({ triggerDiscountPercent: discount ? null : 20 })}
      />

      {discount && (
        <Input
          aria-label="Discount threshold"
          type="number"
          step="0.01"
          defaultValue={step.triggerDiscountPercent ?? 20}
          className="h-8 w-20 min-w-20 py-0 text-right tabular"
          onBlur={(event) => onChange({ triggerDiscountPercent: Number(event.target.value) })}
        />
      )}

      <Chip
        on={step.triggerOnTermsOverride}
        label="Amended terms"
        onClick={() => onChange({ triggerOnTermsOverride: !step.triggerOnTermsOverride })}
      />
    </div>
  )
}

function Chip({ on, label, onClick }: { on: boolean; label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={cx(
        'rounded-full border px-2.5 py-1 text-[11.5px] transition-colors',
        on
          ? 'border-transparent bg-accent-wash text-accent'
          : 'border-line-soft bg-sunken text-ink-2 hover:text-ink',
      )}
    >
      {label}
    </button>
  )
}

